import { resolveNotificationDeepLink } from '@/core/lib/notification-deep-link';
import { runWithNavigationLeaveGate } from '@/core/lib/navigation-leave-gate';
import { scanExchangeDebug } from '@/core/lib/scan-exchange-debug';
import {
  isStackedShellChild,
  resolveShellNavigationPlan,
  type ShellNavigationIntent,
  type ShellNavigationPlan,
} from '@/core/lib/shell-navigation-intent';
import {
  canonicalizeShellPathname,
  isPublicProfilePath,
} from '@/core/router/app-paths';
import { resolveTabForPath } from '@/core/router/app-tabs';
import {
  getShellNav,
  waitForShellNav,
} from '@/core/router/shell-nav-controller';
import { delay } from '@/core/utils/timeout.utils';

/** Let Ionic finish resetTab/changeTab before pushing the scanned profile. */
const TAB_STACK_SETTLE_MS = 250;

export type ShellIonRouter = {
  push: (
    pathname: string,
    routerDirection?: 'forward' | 'back' | 'root' | 'none',
    routerAction?: 'push' | 'pop' | 'replace',
    routeOptions?: unknown,
  ) => void;
};

export type NavigateShellOptions = {
  intent?: ShellNavigationIntent;
  coldLink?: boolean;
  state?: unknown;
  routeOptions?: unknown;
};

export type NavigateShellContext = {
  currentPathname: string;
  currentSearch: string;
  isNative: boolean;
};

function mergeRouteOptions(
  options?: NavigateShellOptions,
): unknown | undefined {
  if (!options) {
    return undefined;
  }

  const hasState = 'state' in options;
  if (!hasState && !options.routeOptions) {
    return undefined;
  }

  const base =
    options.routeOptions && typeof options.routeOptions === 'object'
      ? { ...(options.routeOptions as Record<string, unknown>) }
      : {};

  if (hasState) {
    base.state = options.state;
  }

  return base;
}

async function executeShellNavigationPlan(
  router: ShellIonRouter,
  plan: ShellNavigationPlan,
  context: NavigateShellContext,
  routeOptions?: unknown,
): Promise<void> {
  const targetPath = plan.steps[plan.steps.length - 1]?.path ?? '';
  const targetPathname = canonicalizeShellPathname(targetPath);
  const currentPathname = canonicalizeShellPathname(context.currentPathname);
  let steps = plan.steps;

  if (plan.intent === 'coldDetail') {
    const parentPath = plan.steps[0]?.path;
    const targetTab = resolveTabForPath(targetPathname);
    // Seed the owning tab stack via NavContext. Replaying the parent replace
    // through useIonRouter (especially cross-tab) leaves detail with no
    // history — header backHref still works, but iOS swipe-back does not.
    if (parentPath && targetTab) {
      const shellNav = await waitForShellNav();
      if (shellNav?.resetTab) {
        shellNav.resetTab(targetTab, parentPath, undefined);
        await delay(TAB_STACK_SETTLE_MS);
        shellNav.changeTab(targetTab, parentPath, undefined);
        await delay(TAB_STACK_SETTLE_MS);
        steps = plan.steps.slice(1);
      }
    }
  }

  if (plan.intent === 'switchTab') {
    const shellNav = getShellNav();
    const currentTab = resolveTabForPath(currentPathname);
    const targetTab = resolveTabForPath(targetPathname);

    // resetTab only when popping a stacked child (detail → list). Sibling section
    // switches (e.g. reputation overview ↔ inbox) use root+replace to avoid
    // Ionic tab-stack reset animations on desktop and native.
    const shouldResetTab =
      shellNav?.resetTab &&
      currentTab &&
      targetTab &&
      currentTab === targetTab &&
      targetPathname !== currentPathname &&
      isStackedShellChild(targetPathname, currentPathname);

    if (shouldResetTab) {
      shellNav.resetTab(currentTab, targetPath, undefined);
      return;
    }
  }

  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    if (!step) continue;
    if (index > 0) {
      await delay(100);
    }

    const isLastStep = index === steps.length - 1;
    router.push(
      step.path,
      step.direction,
      step.action,
      isLastStep ? routeOptions : undefined,
    );
  }
}

/**
 * Navigate within the authenticated shell (`/tabs/**`) using Ionic tab stacks.
 */
export async function navigateShell(
  router: ShellIonRouter,
  pathOrDeepLink: string,
  context: NavigateShellContext,
  options?: NavigateShellOptions,
): Promise<void> {
  const fullPath = options?.coldLink
    ? resolveNotificationDeepLink(pathOrDeepLink)
    : pathOrDeepLink.startsWith('/tabs')
      ? pathOrDeepLink
      : resolveNotificationDeepLink(pathOrDeepLink);

  const plan = resolveShellNavigationPlan({
    currentPathname: context.currentPathname,
    currentSearch: context.currentSearch,
    targetPath: fullPath,
    isNative: context.isNative,
    intent: options?.intent,
    coldLink: options?.coldLink,
  });

  if (
    isPublicProfilePath(canonicalizeShellPathname(fullPath.split('?')[0] ?? ''))
  ) {
    scanExchangeDebug('shell.navigate-public-profile', {
      inputPath: pathOrDeepLink,
      resolvedPath: fullPath,
      currentPathname: context.currentPathname,
      currentSearch: context.currentSearch,
      coldLink: Boolean(options?.coldLink),
      intent: plan.intent,
      steps: plan.steps,
    });
  }

  const routeOptions = mergeRouteOptions(options);
  await new Promise<void>((resolve, reject) => {
    runWithNavigationLeaveGate(() => {
      void executeShellNavigationPlan(router, plan, context, routeOptions)
        .then(resolve)
        .catch(reject);
    });
  });
}
